import type { WelcomeOfferStage } from "@/hooks/useWelcomeOffers"

export interface ExportableUser {
  public_id: string
  name: string
  email: string
  email_verified: boolean
  is_superuser: boolean
  teams: Array<{ name: string; role: string }>
  diagram_count: number
  welcome_offer_stage?: WelcomeOfferStage | null
  welcome_offer_status: "shown" | "accepted" | "dismissed" | "emailed" | "onboarded" | "cold" | null
  last_seen_at: string | null
  created_at: string
}

const USER_EXPORT_HEADERS = [
  "Name",
  "Email",
  "Status",
  "Access",
  "Teams",
  "Diagrams",
  "Offer",
  "Last seen",
  "Joined",
]

// Mirrors the on-screen "Offer" column's labels (Users.tsx) so the CSV reads the same. Labels
// are just the state names (owner decision 2026-09-24) — emailed/onboarded/cold are the
// admin-set follow-up states after a coach accepts.
function welcomeOfferCell(status: ExportableUser["welcome_offer_status"] | WelcomeOfferStage): string {
  if (status === "details_received") return "Details received"
  if (status === "accepted") return "Accepted"
  if (status === "shown") return "Shown"
  if (status === "dismissed") return "Dismissed"
  if (status === "emailed") return "Emailed"
  if (status === "onboarded") return "Onboarded"
  if (status === "cold") return "Cold"
  return ""
}

function csvCell(value: string | number): string {
  const text = String(value)
  const spreadsheetSafeText = /^\s*[=+\-@]/.test(text) ? `'${text}` : text

  if (/[",\n\r]/.test(spreadsheetSafeText)) {
    return `"${spreadsheetSafeText.replace(/"/g, "\"\"")}"`
  }

  return spreadsheetSafeText
}

function exportTimestamp(value: string | null): string {
  if (!value) return ""

  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value)
  const iso = value.includes("T") ? value : value.replace(" ", "T")
  const date = new Date(hasZone ? iso : `${iso}Z`)

  return Number.isNaN(date.getTime()) ? value : date.toISOString()
}

function teamsCell(user: ExportableUser): string {
  if (user.teams.length === 0) return "None"
  return user.teams.map((team) => `${team.name} (${team.role})`).join("; ")
}

function timestampMs(value: string | null): number {
  if (!value) return Number.NEGATIVE_INFINITY

  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value)
  const iso = value.includes("T") ? value : value.replace(" ", "T")
  const time = new Date(hasZone ? iso : `${iso}Z`).getTime()

  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time
}

function compareTimestampsNewestFirst(a: string | null, b: string | null): number {
  const aTime = timestampMs(a)
  const bTime = timestampMs(b)

  if (aTime === bTime) return 0
  return aTime > bTime ? -1 : 1
}

export function sortUsersForExport<T extends ExportableUser>(
  users: T[],
  sort: "created_at" | "last_seen"
): T[] {
  return [...users].sort((a, b) => {
    if (sort === "last_seen") {
      const byLastSeen = compareTimestampsNewestFirst(a.last_seen_at, b.last_seen_at)
      if (byLastSeen !== 0) return byLastSeen
    }

    const byJoined = compareTimestampsNewestFirst(a.created_at, b.created_at)
    if (byJoined !== 0) return byJoined

    return a.public_id.localeCompare(b.public_id)
  })
}

export function buildUsersCsv(users: ExportableUser[]): string {
  const rows = users.map((user) => [
    user.name,
    user.email,
    user.email_verified ? "Verified" : "Unverified",
    user.is_superuser ? "Admin" : "User",
    teamsCell(user),
    user.diagram_count ?? 0,
    welcomeOfferCell(user.welcome_offer_stage ?? user.welcome_offer_status),
    exportTimestamp(user.last_seen_at),
    exportTimestamp(user.created_at),
  ])

  return [USER_EXPORT_HEADERS, ...rows]
    .map((row) => row.map(csvCell).join(","))
    .join("\n")
}

export function downloadUsersCsv(users: ExportableUser[]): void {
  const csv = buildUsersCsv(users)
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" })
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement("a")

  anchor.href = url
  anchor.download = `users-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(url)
}
